import { createContext, useContext, useMemo, useState } from 'react'
import { encodeFunctionData, type Abi, type Hash } from 'viem'
import { useAccount, useWriteContract } from 'wagmi'
import { baseSepolia } from 'wagmi/chains'

// Pont vers le smart wallet Privy (ERC-4337, gas sponsorisé par le paymaster
// configuré au dashboard). Publié par PrivyRoot quand l'utilisateur a un wallet
// embarqué ET que les smart wallets sont activés côté dashboard ; null sinon
// (wallet externe, mode classique, ou dashboard pas encore configuré) — dans ce
// cas tout retombe sur wagmi à l'identique.

export type SmartWalletBridge = {
  address: `0x${string}`
  sendTransaction: (tx: { to: `0x${string}`; data: `0x${string}`; value?: bigint }) => Promise<Hash>
}

export const SmartWalletContext = createContext<SmartWalletBridge | null>(null)

/** Identité on-chain du joueur : smart wallet Privy si présent, sinon EOA wagmi. */
export function useGameAccount() {
  const smart = useContext(SmartWalletContext)
  const { address, chainId, isConnected } = useAccount()
  if (smart) {
    // le client smart wallet est créé sur la chaîne par défaut (Base Sepolia)
    return { address: smart.address, chainId: baseSepolia.id, isConnected: true, isSmart: true }
  }
  return { address, chainId, isConnected, isSmart: false }
}

type WriteArgs = {
  abi: Abi | readonly unknown[]
  address: `0x${string}`
  functionName: string
  args?: readonly unknown[]
  value?: bigint
}

type GameWrite = {
  writeContract: (args: WriteArgs) => void
  data: Hash | undefined
  isPending: boolean
  error: Error | null
  reset: () => void
}

/**
 * Drop-in de `useWriteContract` : même forme de retour, mais si un smart wallet
 * Privy est présent la transaction part en user-op sponsorisée (le hash retourné
 * est celui de la transaction incluse — `useWaitForTransactionReceipt` marche tel quel).
 */
export function useGameWrite(): GameWrite {
  const smart = useContext(SmartWalletContext)
  const classic = useWriteContract()
  const [hash, setHash] = useState<Hash | undefined>(undefined)
  const [isPending, setPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  return useMemo(() => {
    if (!smart) {
      return {
        writeContract: classic.writeContract as unknown as (args: WriteArgs) => void,
        data: classic.data,
        isPending: classic.isPending,
        error: (classic.error as Error | null) ?? null,
        reset: classic.reset,
      }
    }
    return {
      writeContract: (args: WriteArgs) => {
        setPending(true)
        setError(null)
        setHash(undefined)
        smart
          .sendTransaction({
            to: args.address,
            data: encodeFunctionData({
              abi: args.abi as Abi,
              functionName: args.functionName,
              args: (args.args ?? []) as unknown[],
            }),
            value: args.value,
          })
          .then(setHash)
          .catch((e) => setError(e instanceof Error ? e : new Error(String(e))))
          .finally(() => setPending(false))
      },
      data: hash,
      isPending,
      error,
      reset: () => {
        setHash(undefined)
        setError(null)
      },
    }
  }, [smart, classic.writeContract, classic.data, classic.isPending, classic.error, classic.reset, hash, isPending, error])
}
